import ToggleInput from '@atoms/ToggleInput/ToggleInput';
import VerticalDirectionEnum from '@models/enums/VerticalDirectionEnum';
import { IContentFilters } from '@models/IContentFilters';
import Dropdown from '@molecules/Dropdown/Dropdown';
import ContentFilters from '@organisms/ContentFilters/ContentFilters';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

interface Props extends IContentFilters {
  readonly showKitsHandler: (value: boolean) => void;
  readonly sortHandler: (value: string) => void;
  readonly showFacets: boolean;
  readonly showKitsOnlyLabel: string;
  readonly showOnlyKits: boolean;
  readonly sortByLabel: string;
  readonly sortOptions: Record<string, string>;
  readonly sortByParam: string;
  readonly totalNumberKitsLabel: string;
  readonly totalNumberResults: number;
  readonly totalNumberResultsLabel: string;
  readonly singleResultLabel: string;
  readonly singleKitLabel: string;
  readonly pageType?: string;
}

const ListingTopBar = (props: Props) => {
  const {
    showExpandedFilters,
    loading,
    pageType,
    showFacets,
    showKitsHandler,
    showKitsOnlyLabel,
    showOnlyKits = false,
    singleKitLabel,
    singleResultLabel,
    sortByLabel,
    sortByParam,
    sortHandler,
    sortOptions = {},
    totalNumberKitsLabel,
    totalNumberResults,
    totalNumberResultsLabel,
  } = props;
  const isMobile = useMediaQuery(1024);
  const [isSortByOpen, setSortByOpen] = useState(false);
  const [activeSortLabel, setActiveSortLabel] = useState('');
  const sortByLabels = Object.keys(sortOptions).map((label) => label);

  useEffect(() => {
    setActiveSortLabel(
      Object.keys(sortOptions).find(
        (label: string) => sortOptions[label] === sortByParam
      ) || ''
    );
  }, [sortOptions, sortByParam]);

  let tagValue: string | undefined;
  if (showOnlyKits) {
    tagValue =
      totalNumberResults === 0 || totalNumberResults > 1
        ? totalNumberKitsLabel
        : singleKitLabel;
  } else {
    tagValue =
      totalNumberResults === 0 || totalNumberResults > 1
        ? totalNumberResultsLabel
        : singleResultLabel;
  }

  return (
    <motion.div
      animate={loading ? { opacity: 0 } : { opacity: 1 }}
      initial={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="flex w-full flex-wrap items-center gap-x-4 gap-y-4 lg:gap-x-8"
    >
      <div className="h6 w-full dark:text-white lg:w-auto">
        <span>{totalNumberResults}</span>
        {tagValue && <span className="ml-2">{tagValue}</span>}
      </div>

      <Dropdown
        active={!!activeSortLabel}
        links={sortByLabels}
        title={activeSortLabel || sortByLabel}
        isOpen={isSortByOpen}
        modalTitle={sortByLabel}
        setIsOpen={setSortByOpen}
        activeIndex={sortByLabels.indexOf(activeSortLabel)}
        openFromDirection={VerticalDirectionEnum.Down}
        itemCallback={(label: string) => sortHandler(sortOptions[label] || '')}
        dataLayerCallback={(label) => {
          updateDataLayer({
            event: 'filterUsed',
            content_type: pageType,
            filter_action: 'sort_filter',
            filter: [{ filter_category: label, filter_name: pageType }],
          });
        }}
      />

      {showKitsOnlyLabel && !isMobile && (
        <div>
          <ToggleInput
            checked={showOnlyKits}
            label={showKitsOnlyLabel}
            labelClassNames="body-two whitespace-nowrap text-white"
            name="showOnlyKits"
            onChange={(isActive) => {
              showKitsHandler(isActive);
            }}
          />
        </div>
      )}

      {showFacets && (
        <ContentFilters
          {...props}
          showExpandedFilters={showExpandedFilters}
          classes="lg:order-last lg:ml-auto"
        />
      )}

      {showKitsOnlyLabel && isMobile && (
        <div>
          <ToggleInput
            checked={showOnlyKits}
            label={showKitsOnlyLabel}
            labelClassNames="body-two whitespace-nowrap text-white"
            name="showOnlyKits"
            onChange={(isActive) => {
              showKitsHandler(isActive);
            }}
          />
        </div>
      )}
    </motion.div>
  );
};

export default ListingTopBar;
